import { FC, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getThirdPartyImportedStartDate, getThirdPartyImportedEndDate } from '../../../store/settings/selectors';
import DatePicker from "react-datepicker";
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { CallApi } from '../../../saga/callApi';
import { ACCOUNTING_SYSTEM_API_BASE_URL, API_URLS, expectedGLRangeForQBOAccount, rootQBOAncestor, QBOClassificationMap } from '../../../resources/constants';
import '../../../assets/css/datepicker.css';
import closeBtn from '../../../assets/images/close-btn.svg';

interface Props {
    branchId: number;
    closeModal: () => void;
}

interface ValidationReport {
    qbo_connection_status?: string;
    trial_balance: {
        passed: boolean;
        debit_sum: number;
        credit_sum: number;
        difference: number;
    };
    balance_sheet: {
        passed: boolean;
        assets_sum: number;
        liabilities_equity_sum: number;
        difference: number;
    };
    net_income: {
        passed: boolean;
        local_net_income: number;
        qbo_net_income: number;
        drift: number;
    };
    mappings: {
        passed: boolean;
        unmapped_count: number;
        unmapped_transactions?: {
            id: number;
            transaction_at: string | null;
            description: string | null;
            amount: number;
            txn_type: string;
        }[];
    };
    period_lock: {
        passed: boolean;
        lock_violations: number;
        max_lock_date: string | null;
    };
}

const QBValidationReportModal: FC<Props> = ({ branchId, closeModal }) => {
    const { t } = useTranslation('components.Modal.QBValidationReport');
    const navigate = useNavigate();
    const importedStartDate = useSelector(getThirdPartyImportedStartDate);
    const importedEndDate = useSelector(getThirdPartyImportedEndDate);

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    
    const [report, setReport] = useState<ValidationReport | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [qboAccounts, setQboAccounts] = useState<any[]>([]);
    const [classificationMap, setClassificationMap] = useState<QBOClassificationMap | null>(null);
    const [qboLoading, setQboLoading] = useState<boolean>(false);
    const [isOpenMappings, setIsOpenMappings] = useState<boolean>(false);
    const [isOpenUnmapped, setIsOpenUnmapped] = useState<boolean>(false);

    const runValidation = async (start: Date, end: Date) => {
        setIsLoading(true);
        setQboLoading(true);
        setError(null);
        try {
            const startStr = moment(start).format('YYYY-MM-DD');
            const endStr = moment(end).format('YYYY-MM-DD');
            const url = `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo-validator/run?branch_id=${branchId}&start_date=${startStr}&end_date=${endStr}`;
            
            const response: any = await CallApi.GET(url, {}, true);
            if (response && response.data && response.data.success) {
                setReport(response.data.data);
            } else {
                setError(t('error.loadFailed'));
            }

            // Fetch QBO account charts for COA mapping status. Paged deliberately: this
            // used to request a single page of 100, so any chart larger than that was
            // validated only in part while still reporting an "N of M" that looked whole.
            try {
                const PER_PAGE = 200;
                const MAX_PAGES = 50; // guard: a bad `total` must not spin forever
                const allAccounts: any[] = [];

                for (let page = 1; page <= MAX_PAGES; page++) {
                    const coaUrl = `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/account/charts?branch_id=${branchId}&page=${page}&per_page=${PER_PAGE}`;
                    const coaResponse: any = await CallApi.GET(coaUrl, {}, true);
                    const body = coaResponse?.data?.data || coaResponse?.data;
                    const batch = body?.result || (Array.isArray(body) ? body : []);
                    if (!Array.isArray(batch) || batch.length === 0) break;

                    allAccounts.push(...batch);

                    const total = Number(body?.total);
                    const reachedTotal = Number.isFinite(total) && allAccounts.length >= total;
                    if (reachedTotal || !body?.next || batch.length < PER_PAGE) break;

                    if (page === MAX_PAGES) {
                        console.warn(`Stopped paging QBO accounts at ${allAccounts.length} of ${total} — page cap reached.`);
                    }
                }
                setQboAccounts(allAccounts);
            } catch (coaErr) {
                console.warn("Could not load QBO account charts:", coaErr);
            }

            // Authoritative account-number bands. Without it the COA check falls back to
            // the frontend's own copy in constants.ts, which is a mirror, not the source.
            try {
                const mapResponse: any = await CallApi.GET(API_URLS.QUICK_BOOK.CLASSIFICATION_MAP, {}, true);
                const map = mapResponse?.data?.data || mapResponse?.data;
                if (map && typeof map === 'object') {
                    setClassificationMap(map);
                }
            } catch (mapErr) {
                console.warn("Could not load QBO classification map, using local ranges:", mapErr);
            }
        } catch (err: any) {
            console.error("Error running validation checks:", err);
            setError(err?.data?.message || t('error.unexpected'));
        } finally {
            setIsLoading(false);
            setQboLoading(false);
        }
    };

    useEffect(() => {
        if (branchId) {
            const start = importedStartDate ? moment(importedStartDate).toDate() : moment().startOf('year').toDate();
            const end = importedEndDate ? moment(importedEndDate).toDate() : moment().endOf('month').toDate();
            setStartDate(start);
            setEndDate(end);
            runValidation(start, end);
        }
    }, [branchId, importedStartDate, importedEndDate]);

    const renderCardStatus = (passed: boolean, customType?: 'warning' | 'danger') => {
        if (passed) {
            return (
                <span className="badge bg-success px-3 py-2" style={{ borderRadius: '20px', fontSize: '0.85rem' }}>
                    <i className="fa fa-check-circle me-1" /> {t('status.passed')}
                </span>
            );
        }
        if (customType === 'warning') {
            return (
                <span className="badge px-3 py-2" style={{ borderRadius: '20px', fontSize: '0.85rem', backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #f59e0b' }}>
                    <i className="fa fa-exclamation-triangle me-1" /> {t('status.warning')}
                </span>
            );
        }
        return (
            <span className="badge bg-danger px-3 py-2" style={{ borderRadius: '20px', fontSize: '0.85rem' }}>
                <i className="fa fa-exclamation-triangle me-1" /> {t('status.discrepancyFound')}
            </span>
        );
    };

    return (
        <Modal show={true} onHide={closeModal} backdrop="static" animation={false} size="lg">
            <div className="modal-header" style={{ borderBottom: '1px solid #dee2e6' }}>
                <h5 className="modal-title" style={{ fontWeight: 600, color: '#1e293b' }}>
                    {t('title')}
                </h5>
                <button type="button" className="btn-close" onClick={closeModal}>
                    <img src={closeBtn} alt={t('closeAlt')} />
                </button>
            </div>
            
            <div className="modal-body" style={{ backgroundColor: '#f8fafc', padding: '1.5rem' }}>
                {/* Read-only validation date range info banner */}
                <div className="alert alert-info border-0 shadow-sm mb-4 d-flex align-items-center justify-content-between" style={{ borderRadius: '12px', padding: '1rem' }}>
                    <div className="d-flex align-items-center">
                        <i className="fa fa-calendar-check me-2 text-primary" style={{ fontSize: '1.25rem' }} />
                        <div>
                            <span className="fw-semibold text-dark">{t('period.label')}</span>
                            <span className="ms-2 text-muted fw-bold">
                                {startDate ? moment(startDate).format('MM/DD/YYYY') : t('common.notAvailable')} - {endDate ? moment(endDate).format('MM/DD/YYYY') : t('common.notAvailable')}
                            </span>
                        </div>
                    </div>
                    <span className="badge bg-primary px-3 py-2 fw-medium" style={{ borderRadius: '20px' }}>
                        {t('period.importRange')}
                    </span>
                </div>

                {isLoading && (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5">
                        <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status" />
                        <span className="text-muted fw-semibold">{t('loading.runningChecks')}</span>
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger d-flex align-items-center" role="alert">
                        <i className="fa fa-exclamation-circle me-2" />
                        <div>{error}</div>
                    </div>
                )}

                {!isLoading && report && report.qbo_connection_status === "disconnected" && (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
                        <i className="fa fa-exclamation-triangle text-danger mb-3" style={{ fontSize: '3.5rem' }}></i>
                        <h4 className="fw-bold text-dark mb-2">{t('disconnected.title')}</h4>
                        <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '400px' }}>
                            {t('disconnected.message')}
                        </p>
                        <button
                            type="button"
                            className="btn btn-primary px-4 py-2"
                            disabled={qboLoading}
                            onClick={async () => {
                                try {
                                    setQboLoading(true);
                                    localStorage.setItem('qbo_reconnect_origin', 'dashboard_validation');
                                    const authUrlEndpoint = `${ACCOUNTING_SYSTEM_API_BASE_URL}/api/v1/qbo/auth/url?branch_id=${branchId}`;
                                    const authUrlResponse: any = await CallApi.GET(authUrlEndpoint, {}, true);
                                    if (authUrlResponse && authUrlResponse.data && authUrlResponse.data.success) {
                                        const authUrl = authUrlResponse.data.data.auth_url;
                                        closeModal();
                                        window.location.href = authUrl;
                                    } else {
                                        setError(t('error.authUrlFailed'));
                                    }
                                } catch (reconnectErr: any) {
                                    console.error("Error generating QBO auth URL:", reconnectErr);
                                    setError(t('error.authUrlError'));
                                } finally {
                                    setQboLoading(false);
                                }
                            }}
                        >
                            {qboLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {t('disconnected.connecting')}
                                </>
                            ) : (
                                t('disconnected.reconnect')
                            )}
                        </button>
                    </div>
                )}

                {!isLoading && report && report.qbo_connection_status !== "disconnected" && (() => {
                    const activeQboAccounts = qboAccounts.filter(acc => acc.Active === 1 || acc.Active === true || acc.Active === null);
                    
                    // Indexed over every account, not just the active ones: an active
                    // sub-account can hang off an inactive parent, and losing that parent
                    // would drop the child back to judging itself by its own AccountType.
                    const accountsById = new Map<string, any>(
                        qboAccounts
                            .filter(a => a?.Id_qbo != null)
                            .map(a => [String(a.Id_qbo), a]),
                    );

                    const getMappingIssue = (acc: any) => {
                        const isMapped = acc.gl_mapping_account_id && acc.gl_mapping_account_id !== 0;
                        if (!isMapped) {
                            return "Not Mapped";
                        }

                        if (acc.account_chart && acc.account_chart.account) {
                            const glAccNum = parseInt(acc.account_chart.account, 10);
                            const anchor = rootQBOAncestor(acc, accountsById);
                            const range =
                                expectedGLRangeForQBOAccount(anchor.AccountType, anchor.AccountSubType, anchor.NAME || anchor.Name, classificationMap)
                                || expectedGLRangeForQBOAccount(acc.AccountType, acc.AccountSubType, acc.NAME || acc.Name, classificationMap);

                            if (range && (glAccNum < range[0] || glAccNum > range[1])) {
                                return "Account Type Mismatch";
                            }
                        }
                        return null; // Mapped correctly!
                    };

                    const coaIssues = activeQboAccounts.map(acc => ({
                        acc,
                        issue: getMappingIssue(acc)
                    })).filter(x => x.issue !== null);

                    const totalCOA = activeQboAccounts.length;
                    const unmappedCOACount = coaIssues.length;
                    const isCOAMappedPassed = totalCOA > 0 && unmappedCOACount === 0;

                    return (
                        <div className="d-flex flex-column gap-3">
                            {/* 1. Account Mappings Status (COA Mapping) */}
                            <div className="card border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                                <div className="card-body p-3">
                                    <div className="d-flex align-items-center justify-content-between cursor-pointer" onClick={() => setIsOpenMappings(!isOpenMappings)}>
                                        <div>
                                            <h6 className="mb-1" style={{ fontWeight: 600, color: '#334155' }}>
                                                {t('mappings.title')}
                                                <i className={`fa ${isOpenMappings ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2 text-muted small`} />
                                            </h6>
                                            <p className="text-muted small mb-0">
                                                {t('mappings.description')}
                                                {isCOAMappedPassed ? (
                                                    <span className="text-success ms-1 fw-medium">{t('mappings.allMapped')}</span>
                                                ) : (
                                                    <span className="ms-1 fw-medium" style={{ color: '#b45309' }}>{t('mappings.issues', { count: unmappedCOACount, total: totalCOA })}</span>
                                                )}
                                            </p>
                                        </div>
                                        {renderCardStatus(isCOAMappedPassed, 'warning')}
                                    </div>

                                    {isOpenMappings && (
                                        <div className="mt-3 border-top pt-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="small fw-bold text-secondary">{t('mappings.resultTitle')}</span>
                                            </div>
                                            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>{t('mappings.table.qboAccount')}</th>
                                                            <th>{t('mappings.table.accountType')}</th>
                                                            <th>{t('mappings.table.glAccount')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {activeQboAccounts.length === 0 ? (
                                                            <tr>
                                                                <td colSpan={3} className="text-center text-muted py-2">{t('mappings.empty')}</td>
                                                            </tr>
                                                        ) : (
                                                            activeQboAccounts.map((acc, index) => {
                                                                 const issue = getMappingIssue(acc);
                                                                 return (
                                                                     <tr key={index}>
                                                                         <td className="fw-medium" style={acc.ParentRef_value ? { paddingLeft: '1.5rem' } : {}}>
                                                                             {acc.ParentRef_value ? "└─ " : ""}
                                                                             {acc.FullyQualifiedName || acc.NAME}
                                                                         </td>
                                                                         <td>{acc.AccountType}</td>
                                                                         <td>
                                                                             {issue === null && acc.account_chart ? (
                                                                                 <span className="text-success">
                                                                                     <i className="fa fa-link me-1" />
                                                                                     {acc.account_chart.name} ({acc.account_chart.account})
                                                                                 </span>
                                                                             ) : issue === "Not Mapped" ? (
                                                                                 <span className="fw-semibold" style={{ color: '#b45309' }}>
                                                                                     <i className="fa fa-unlink me-1" />
                                                                                     {t('mappings.notMapped')}
                                                                                 </span>
                                                                             ) : (
                                                                                 <span className="fw-semibold" style={{ color: '#b45309' }} title={t('mappings.typeMismatchTooltip')}>
                                                                                     <i className="fa fa-exclamation-circle me-1" />
                                                                                     {t('mappings.typeMismatch')}
                                                                                 </span>
                                                                             )}
                                                                         </td>
                                                                     </tr>
                                                                );
                                                            })
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 2. Unmapped Transactions */}
                            <div className="card border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                                <div className="card-body p-3">
                                    <div className="d-flex align-items-center justify-content-between cursor-pointer" onClick={() => setIsOpenUnmapped(!isOpenUnmapped)}>
                                        <div>
                                            <h6 className="mb-1" style={{ fontWeight: 600, color: '#334155' }}>
                                                {t('unmapped.title')}
                                                <i className={`fa ${isOpenUnmapped ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2 text-muted small`} />
                                            </h6>
                                            <p className="text-muted small mb-0">
                                                {t('unmapped.description')}
                                                {report.mappings.passed ? (
                                                    <span className="text-success ms-1 fw-medium">{t('unmapped.allMapped')}</span>
                                                ) : (
                                                    <span className="text-danger ms-1 fw-medium">{t('unmapped.pending', { count: report.mappings.unmapped_count })}</span>
                                                )}
                                            </p>
                                        </div>
                                        {renderCardStatus(report.mappings.passed)}
                                    </div>

                                    {isOpenUnmapped && (
                                        <div className="mt-3 border-top pt-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="small fw-bold text-secondary">{t('unmapped.splitsTitle')}</span>
                                            </div>
                                            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.85rem' }}>
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>{t('unmapped.table.date')}</th>
                                                            <th>{t('unmapped.table.description')}</th>
                                                            <th>{t('unmapped.table.type')}</th>
                                                            <th>{t('unmapped.table.amount')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {!report.mappings.unmapped_transactions || report.mappings.unmapped_transactions.filter(tx => tx.amount > 0).length === 0 ? (
                                                            <tr>
                                                                <td colSpan={4} className="text-center text-muted py-2">{t('unmapped.empty')}</td>
                                                            </tr>
                                                        ) : (
                                                            report.mappings.unmapped_transactions.filter(tx => tx.amount > 0).map((tx, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{tx.transaction_at || t('common.notAvailable')}</td>
                                                                    <td className="fw-medium">{tx.description || t('common.notAvailable')}</td>
                                                                    <td>
                                                                        <span className={`badge ${tx.txn_type === 'Debit' ? 'bg-light text-primary' : 'bg-light text-success'}`}>
                                                                            {tx.txn_type === 'Debit' ? t('txnType.debit') : tx.txn_type === 'Credit' ? t('txnType.credit') : tx.txn_type}
                                                                        </span>
                                                                    </td>
                                                                    <td className="fw-bold">${tx.amount.toLocaleString()}</td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 3. Trial Balance Check */}
                            <div className="card border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                                <div className="card-body d-flex align-items-center justify-content-between p-3">
                                    <div>
                                        <h6 className="mb-1" style={{ fontWeight: 600, color: '#334155' }}>{t('trialBalance.title')}</h6>
                                        <p className="text-muted small mb-0">
                                            {t('trialBalance.description')}
                                            {report.trial_balance.passed ? (
                                                <span className="text-success ms-1 fw-medium">{t('trialBalance.passed', { debits: report.trial_balance.debit_sum.toLocaleString(), credits: report.trial_balance.credit_sum.toLocaleString() })}</span>
                                            ) : (
                                                <span className="text-danger ms-1 fw-medium">{t('trialBalance.failed', { difference: report.trial_balance.difference.toLocaleString(), debits: report.trial_balance.debit_sum.toLocaleString(), credits: report.trial_balance.credit_sum.toLocaleString() })}</span>
                                            )}
                                        </p>
                                    </div>
                                    {renderCardStatus(report.trial_balance.passed)}
                                </div>
                            </div>

                            {/* 4. Balance Sheet Check */}
                            <div className="card border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                                <div className="card-body d-flex align-items-center justify-content-between p-3">
                                    <div>
                                        <h6 className="mb-1" style={{ fontWeight: 600, color: '#334155' }}>{t('balanceSheet.title')}</h6>
                                        <p className="text-muted small mb-0">
                                            {t('balanceSheet.description')}
                                            {report.balance_sheet.passed ? (
                                                <span className="text-success ms-1 fw-medium">{t('balanceSheet.passed', { assets: report.balance_sheet.assets_sum.toLocaleString(), liabilitiesEquity: report.balance_sheet.liabilities_equity_sum.toLocaleString() })}</span>
                                            ) : report.balance_sheet.difference <= 1.0 ? (
                                                <span className="ms-1 fw-medium" style={{ color: '#b45309' }}>{t('balanceSheet.minorDifference', { difference: report.balance_sheet.difference.toLocaleString(), assets: report.balance_sheet.assets_sum.toLocaleString(), liabilitiesEquity: report.balance_sheet.liabilities_equity_sum.toLocaleString() })}</span>
                                            ) : (
                                                <span className="text-danger ms-1 fw-medium">{t('balanceSheet.failed', { difference: report.balance_sheet.difference.toLocaleString(), assets: report.balance_sheet.assets_sum.toLocaleString(), liabilitiesEquity: report.balance_sheet.liabilities_equity_sum.toLocaleString() })}</span>
                                            )}
                                        </p>
                                    </div>
                                    {renderCardStatus(
                                        report.balance_sheet.passed,
                                        !report.balance_sheet.passed && report.balance_sheet.difference <= 1.0 ? 'warning' : undefined
                                    )}
                                </div>
                            </div>

                            {/* 5. Net Income Sync Check (Hidden) */}
                            {/* <div className="card border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                                <div className="card-body d-flex align-items-center justify-content-between p-3">
                                    <div>
                                        <h6 className="mb-1" style={{ fontWeight: 600, color: '#334155' }}>Net Income Sync Status</h6>
                                        <p className="text-muted small mb-0">
                                            Compares local Net Income against live QBO Profit & Loss Net Income.
                                            {report.net_income.passed ? (
                                                <span className="text-success ms-1 fw-medium">(Synced at ${report.net_income.local_net_income.toLocaleString()})</span>
                                            ) : (
                                                <span className="text-danger ms-1 fw-medium">(Out of Sync by ${report.net_income.drift.toLocaleString()} | Local: ${report.net_income.local_net_income.toLocaleString()} vs QBO Live: ${report.net_income.qbo_net_income.toLocaleString()})</span>
                                            )}
                                        </p>
                                    </div>
                                    {renderCardStatus(report.net_income.passed)}
                                </div>
                            </div> */}
                        </div>
                    );
                })()}
            </div>

            <div className="modal-footer" style={{ borderTop: '1px solid #dee2e6' }}>
                <button type="button" className="btn btn-primary px-4" onClick={closeModal}>
                    {t('button.ok')}
                </button>
            </div>
        </Modal>
    );
};

export default QBValidationReportModal;
